import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Building2,
  DollarSign,
  Key,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  type LucideIcon,
  MapPin,
  Network,
  Rocket,
  ScrollText,
  ShieldCheck,
  Store,
  UserCheck,
  UserCircle2,
  Users,
} from "lucide-react";
import { NavLink } from "react-router-dom";

import { apiClient } from "@/lib/api";
import { brandDisplay } from "@/lib/brand";
import {
  canCreateAnySubPartner,
  canOpenImpersonatePage,
  hasAnyLocationTierGrant,
  hasAnyOrgTierGrant,
  hasAnyPartnerTierGrant,
  isSuperAdmin,
  type RoleProfile,
} from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";

import { usePartnerScope } from "./partner-scope-context";

interface NavGateInput {
  profile: RoleProfile | undefined;
  /** Partners in the caller's scope - used for flag-aware gates. */
  partners: Array<{ path: string; can_manage_sub_partners: boolean; can_impersonate: boolean }>;
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * When set, the item only renders if the caller passes this
   * predicate. Gates read both the acting-as profile AND the caller's
   * visible partners list so flag-aware rules (impersonate, cascade)
   * can drop the nav entry when nothing it links to is actionable
   * for the caller. Server-side enforcement is still the source of
   * truth - these gates just keep the sidebar clean.
   */
  visibleWhen?: (ctx: NavGateInput) => boolean;
}

interface NavGroup {
  /** Section header. Omit for the top group so the most-used items
   *  read as the page-title cluster. */
  label?: string;
  items: NavItem[];
}

/**
 * Sidebar grouping. Order matters - top-down narrates the admin's
 * day: Dashboard / quick actions, then the tenant hierarchy
 * (top-down), then who can use it, then how it's wired, then
 * platform-only screens, then meta. Each group's visibility
 * collapses on its own - empty groups disappear cleanly so a
 * non-super sees a tighter rail.
 */
const NAV_GROUPS: NavGroup[] = [
  // --- top: daily essentials, no header so they sit closest to the brand ---
  {
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      {
        to: "/onboard",
        label: "Quick onboard",
        icon: Rocket,
        visibleWhen: ({ profile, partners }) => canCreateAnySubPartner(profile, partners),
      },
    ],
  },
  // --- tenant hierarchy (top-down: partner → org → location) ---
  {
    label: "Tenants",
    items: [
      {
        // Partners is a partner-TIER view - an org_admin or
        // location_manager has no actionable use of this page (per matrix
        // answer #6: can't touch anything at their parent level). Hide
        // the nav entry entirely (answer #7: "nav hidden, not empty").
        to: "/partners",
        label: "Partners",
        icon: Building2,
        visibleWhen: ({ profile }) => hasAnyPartnerTierGrant(profile),
      },
      {
        // Organizations covers partner-tier callers (sees all orgs in
        // subtree) + org_admins (sees their own org). Loc-scope grants
        // don't qualify - a location_manager has no use of this page.
        to: "/organizations",
        label: "Organizations",
        icon: Store,
        visibleWhen: ({ profile }) => hasAnyOrgTierGrant(profile),
      },
      {
        // Locations is the broadest - any scope grant (partner / org /
        // loc) eventually drills down to at least one location.
        to: "/locations",
        label: "Locations",
        icon: MapPin,
        visibleWhen: ({ profile }) => hasAnyLocationTierGrant(profile),
      },
    ],
  },
  // --- people + access ---
  {
    label: "Access",
    items: [
      {
        // Unified user-management surface - merges admin (user_tbl) + POS
        // (pos_user_tbl) populations into one paginated list with a pool
        // discriminator. The legacy /users page (admin-only) still exists
        // for the Invite admin flow but new navigation lands here.
        // Server-side visibility is the same shape as the legacy /users
        // route (allowed_scope_paths + sub-partner membership intersection),
        // so an org_admin only sees people under their org and a
        // location_manager only their location's people.
        to: "/people",
        label: "People",
        icon: Users,
        visibleWhen: ({ profile }) => hasAnyLocationTierGrant(profile),
      },
      {
        to: "/impersonate",
        label: "Impersonate",
        icon: UserCheck,
        visibleWhen: ({ profile, partners }) => canOpenImpersonatePage(profile, partners),
      },
    ],
  },
  // --- per-tenant configuration ---
  {
    label: "Configuration",
    items: [
      {
        // Credentials: provider secrets (Valor MIDs, Aeropay keys). A
        // location_manager has no need to see or manage them - payment
        // wiring lives one tier up. Org-or-partner tier callers manage
        // them at their own scope (or higher) via this page.
        to: "/provider-credentials",
        label: "Credentials",
        icon: KeyRound,
        visibleWhen: ({ profile }) => hasAnyOrgTierGrant(profile),
      },
      {
        to: "/platform/catalog",
        label: "Platform catalog",
        icon: LayoutGrid,
        visibleWhen: ({ profile }) => isSuperAdmin(profile),
      },
      {
        // Phase A - read-only role + permission catalog browser. Page
        // is visible to any authenticated admin; the API narrows the
        // visible roles to system + caller's partner subtree, so a
        // partner_admin sees system roles + their own customs.
        to: "/platform/roles",
        label: "Roles & permissions",
        icon: ShieldCheck,
      },
      {
        to: "/platform/permissions",
        label: "Permission catalog",
        icon: Key,
        visibleWhen: ({ profile }) => isSuperAdmin(profile),
      },
    ],
  },
  // --- meta / personal ---
  {
    label: "System",
    items: [
      {
        // V0072 Integration Health surface. Sidebar gate is super_admin
        // by default - partner_admins reach it via direct URL once a
        // delegation row is granted (the API enforces both the link's
        // visibility and the page's data scope; we don't pre-fetch
        // delegations here just to show/hide a nav entry).
        to: "/integrations/health",
        label: "Integration health",
        icon: Activity,
        visibleWhen: ({ profile }) => isSuperAdmin(profile),
      },
      {
        to: "/platform/costs",
        label: "Costs",
        icon: DollarSign,
        visibleWhen: ({ profile }) => isSuperAdmin(profile),
      },
      { to: "/audit", label: "Audit log", icon: ScrollText },
      {
        to: "/system/api-logs",
        label: "API logs",
        icon: Network,
        visibleWhen: ({ profile }) => isSuperAdmin(profile),
      },
      { to: "/profile", label: "Profile", icon: UserCircle2 },
    ],
  },
];

/**
 * Left rail - brand + primary nav. Stays visible on md+ screens and
 * collapses to a drawer toggled from the topbar on narrow viewports.
 * Drawer toggle lives in AppShell so the backdrop can cover the whole
 * content area.
 */
export function Sidebar(props: { onNavigate?: () => void }): JSX.Element {
  const { brand } = usePartnerScope();
  const { logoUrl, productName } = brandDisplay(brand);

  // Role view comes from /profile/acting-as, not /profile -
  // impersonation should swap the sidebar to the target's perspective.
  // PartnerScopeProvider owns the fetch; useRoleProfile reads from
  // the same cache without attaching a new observer (the mobile
  // drawer mounts + unmounts on every open, and re-firing the query
  // there would be wasteful).
  const profile = useRoleProfile();

  // Partners list powers the flag-aware gates (Quick onboard needs
  // can_manage_sub_partners; Impersonate needs can_impersonate). The
  // query is cached with a long stale time because the flags flip
  // rarely. Super_admins short-circuit the gate predicates so an
  // empty list while the query loads doesn't hide their nav during
  // the first render pass.
  //
  // Keyed on the acting-as identity so an impersonation start/stop
  // evicts the cache - otherwise an actor who impersonates a
  // partner_admin could keep seeing the actor's wider nav (or vice
  // versa) until the staleTime elapses. ``primary_partner.id`` is a
  // cheap proxy for identity that changes on every login + every
  // impersonation swap.
  const partnersQuery = useQuery({
    queryKey: ["partners", "sidebar-gates", profile?.primary_partner?.id ?? null],
    // Paginated endpoint caps at 100 per page, which covers every
    // realistic caller's scope. If someone has >100 grants we still
    // render the nav conservatively (reduction over first page).
    queryFn: () => apiClient.listPartners({ limit: 100 }),
    staleTime: 5 * 60_000,
    enabled: !!profile,
  });
  const partners = partnersQuery.data?.partners ?? [];
  // Filter each group's items by their gate, then drop groups that
  // wound up empty so the rendered rail stays tight for limited-grant
  // callers (e.g. an org_admin doesn't see a "Tenants" header with
  // just one row underneath).
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.visibleWhen?.({ profile, partners }) ?? true),
  })).filter((group) => group.items.length > 0);

  return (
    <aside className="flex h-full w-60 flex-col border-r bg-card">
      <div className="flex items-center gap-3 px-4 py-4">
        <img src={logoUrl} alt={productName} className="h-8 w-8 rounded object-contain" />
        <span className="text-sm font-semibold tracking-tight">{productName} Admin</span>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4" aria-label="Primary">
        {visibleGroups.map((group, idx) => (
          <div key={group.label ?? `top-${idx}`} className={cn(idx > 0 && "mt-4")}>
            {group.label && (
              // text-muted-foreground at full opacity is 4.5:1+ on
              // bg-card. The previous /70 dropped contrast to 2.71:1
              // (axe finding) for these tiny 10px section headers.
              <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={props.onNavigate}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-md px-3 py-2 text-sm",
                          isActive
                            ? "bg-primary/10 font-semibold text-foreground dark:bg-primary dark:text-primary-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground",
                        )
                      }
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t px-4 py-3 text-xs text-muted-foreground">
        <p>Admin console · v0.1</p>
      </div>
    </aside>
  );
}
